import { FC } from 'react';
import { Alert, AlertTitle, Button } from '@mui/material';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'src/app/reduxCustomHooks';
import { useLimitAndUsageControllerGetMyUsageQuery } from 'src/app/services/api.generated';

/**
 * Shown to managers while the company's Stripe subscription has an
 * outstanding payment (past_due/unpaid or an uncleared failed invoice).
 * Display only. Once access is suspended (BILLING_SUSPEND_ENABLED),
 * BillingSuspendedBanner takes over and this one is hidden.
 */
export const PaymentIssueBanner: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isManager = useAppSelector((state) => state.auth.isManager);
  // Same cached request as the dashboard usage card (managers only).
  const { data } = useLimitAndUsageControllerGetMyUsageQuery(undefined as void, {
    skip: !isManager,
  });

  if (!isManager || !data?.paymentIssue || data.billingSuspended) return null;

  return (
    <Alert
      severity="warning"
      variant="outlined"
      role="alert"
      data-testid="payment-issue-banner"
      sx={{ mb: { xs: 2, md: 3 }, alignItems: 'center' }}
      action={
        <Button color="inherit" variant="outlined" size="small" sx={{ whiteSpace: 'nowrap' }} onClick={() => navigate('/manage-billing')}>
          {t('paymentIssueBanner.cta')}
        </Button>
      }
    >
      <AlertTitle sx={{ fontWeight: 700 }}>{t('paymentIssueBanner.title')}</AlertTitle>
      {t('paymentIssueBanner.text')}
    </Alert>
  );
};
